//@flow
import React from 'react';
import i18n from 'app/common/lib/i18n';
import ImageOnlyAttribution from 'app/common/react/components/DeprecatedPinRep/attribution/ImageOnlyAttribution';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import type { AttributedPin } from 'app/common/react/components/DeprecatedPinRep/attribution/types';
import { buildPinDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Text } from 'gestalt';

type Props = {|
  feedbackType: string,
  imageOnlyAttribution?: boolean,
  isHovering: boolean,
  isVideo: boolean,
  onClickThrough: (isOneTap?: boolean) => void,
  pin: AttributedPin,
  sourceUrl: string,
|};

export default function AttributionText({
  pin,
  feedbackType,
  imageOnlyAttribution,
  isHovering,
  isVideo,
  onClickThrough,
  sourceUrl,
}: Props) {
  const pinDeeplinkUrls = buildPinDeeplinks(pin.id);
  const sourceAttribution = pin.attribution || {};
  const attributionLink = sourceAttribution.author_url || sourceAttribution.provider_url;
  const authorName = sourceAttribution.author_name;
  const siteName = sourceAttribution.rich_site_name || sourceAttribution.provider_name;
  const attributionText = authorName
    ? interpolateNamedTemplate(i18n._('by {{ authorName }}', 'attribution on pin rep'), {
        authorName,
      })
    : siteName;
  // if there is no attribution text but there is a required attribution provider just use the domain and source url
  const finalAttributionText =
    !attributionText && pin.has_required_attribution_provider ? pin.domain : attributionText;
  const finalAttributionLink = attributionLink || sourceUrl;
  const hasFullAttribution = finalAttributionLink && finalAttributionText;
  const showImageOnlyAttribution =
    isHovering &&
    !hasFullAttribution &&
    imageOnlyAttribution &&
    !isVideo &&
    !pin.is_native &&
    feedbackType !== 'followed';
  const interestName = (pin.source_interest || {}).name;
  const dataTestId = 'io-topic-attr-' + interestName;
  return (
    <React.Fragment>
      {/* Ensure that we properly attribute pins even in Image Only, ping #statemachine before changing */}
      {hasFullAttribution && (
        <Box paddingY={1}>
          <Text size="sm" weight="bold">
            <NavigateLink
              deeplinkUrls={pinDeeplinkUrls}
              external
              onClick={onClickThrough}
              pinId={pin.id}
              stopPropagation
              style={{ color: '#898989' }}
              to={finalAttributionLink}
            >
              {finalAttributionText}
            </NavigateLink>
          </Text>
        </Box>
      )}

      {/* image only attribution */}
      {imageOnlyAttribution && feedbackType && <Box data-test-id={dataTestId} />}
      {showImageOnlyAttribution &&
        feedbackType && (
          <ImageOnlyAttribution
            feedbackType={feedbackType}
            pinner={pin.pinner}
            topic={interestName}
          />
        )}
    </React.Fragment>
  );
}
